/**
 * @poc-to-production — Avatar + account menu (top-right).
 */
import { useEffect, useRef, useState } from "preact/hooks";
import { getApsTier } from "../auth/apsConfig";
import { profileDetailRows } from "../auth/userProfile";
import { useAuthStore } from "../stores/authStore";
import { ProfileAvatar } from "./ProfileAvatar";

function MenuRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        marginTop: 6,
      }}
    >
      <dt style={{ margin: 0 }}>{label}</dt>
      <dd
        style={{
          margin: 0,
          color: "var(--vera-text)",
          textAlign: "right",
          maxWidth: 160,
          wordBreak: "break-word",
        }}
      >
        {value}
      </dd>
    </div>
  );
}

export function UserMenu() {
  const profile = useAuthStore(s => s.profile);
  const expiresAt = useAuthStore(s => s.expiresAt);
  const logout = useAuthStore(s => s.logout);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  if (!profile) return null;

  const tier = getApsTier();
  const details = profileDetailRows(profile);
  const sessionEnds =
    expiresAt != null
      ? new Date(expiresAt + 60_000).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(v => !v)}
        title={profile.displayName}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "2px 8px 2px 2px",
          border: `1px solid ${open ? "var(--vera-accent)" : "var(--vera-border)"}`,
          borderRadius: 20,
          background: "var(--vera-panel-bg)",
          cursor: "pointer",
        }}
      >
        <ProfileAvatar profile={profile} size="sm" />
        <span
          style={{
            fontSize: 12,
            maxWidth: 120,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {profile.displayName}
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: 300,
            maxWidth: 340,
            padding: "12px 0",
            background: "var(--vera-panel-bg)",
            border: "1px solid var(--vera-border)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            zIndex: 200,
          }}
        >
          <div
            style={{
              padding: "0 16px 16px",
              borderBottom: "1px solid var(--vera-border)",
              display: "flex",
              gap: 14,
              alignItems: "center",
            }}
          >
            <ProfileAvatar profile={profile} size="lg" />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.3 }}>
                {profile.displayName}
              </div>
              {profile.email ? (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--vera-text-dim)",
                    marginTop: 4,
                    wordBreak: "break-all",
                  }}
                >
                  {profile.email}
                </div>
              ) : null}
            </div>
          </div>

          <dl
            style={{
              margin: 0,
              padding: "12px 16px",
              fontSize: 11,
              color: "var(--vera-text-dim)",
            }}
          >
            {details.map(row => (
              <MenuRow key={row.label} label={row.label} value={row.value} />
            ))}
            <MenuRow label="Environment" value={tier.toUpperCase()} />
            {profile.userId ? (
              <MenuRow label="User ID" value={profile.userId} />
            ) : null}
            {sessionEnds ? (
              <MenuRow label="Session until" value={sessionEnds} />
            ) : null}
          </dl>

          <div
            style={{
              padding: "8px 12px 0",
              borderTop: "1px solid var(--vera-border)",
            }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                logout();
              }}
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: 12,
                textAlign: "left",
                border: "none",
                borderRadius: 4,
                background: "transparent",
                cursor: "pointer",
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
